<script setup>
  import { onMounted, onUnmounted, watch } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import { storeToRefs } from "pinia";
  import { useMasterReportsStore } from "@stores/masterReports/masterReports";
  import { useChartTemplatesStore } from "@stores/chartTemplates/chartTemplates";
  import { Button } from "@/components/ui/button";
  import { Card, CardContent } from "@/components/ui/card";
  import { FileSpreadsheet } from "lucide-vue-next";
  import { EmptyState } from "@/components/episto-ui/empty-state";
  import { LoadingState } from "@/components/episto-ui/loading-state";
  import { ChartTemplatesList, ChartTemplateEditor } from "@/components/Reports/ChartTemplates";
  import TwoPanePage from "@templates/TwoPanePage.vue";

  const route = useRoute();
  const router = useRouter();
  const masterReportsStore = useMasterReportsStore();
  const chartTemplatesStore = useChartTemplatesStore();

  const { currentMasterReport, loading } = storeToRefs(masterReportsStore);
  const { chartTemplates, allCharts, currentChartTemplateId, currentChartTemplate } =
    storeToRefs(chartTemplatesStore);

  const {
    selectTemplate,
    removeTemplate,
    duplicateTemplate,
    reorderTemplates,
    clearEditState,
    addBlankTemplate,
    setThemeToken,
    fetchTemplates,
    loadAllCharts,
  } = chartTemplatesStore;

  async function initializeForMasterReport(token) {
    await masterReportsStore.fetchMasterReport(token);

    if (!currentMasterReport.value) return;

    setThemeToken(token);
    await fetchTemplates(token);

    if (currentMasterReport.value.pptx_source_id) {
      await loadAllCharts(currentMasterReport.value.pptx_source_id);
    }

    if (chartTemplates.value.length > 0 && !currentChartTemplateId.value) {
      selectTemplate(chartTemplates.value[0].id);
    }
  }

  onMounted(async () => {
    await initializeForMasterReport(route.params.themeToken);
  });

  onUnmounted(() => {
    clearEditState();
  });

  watch(
    () => route.params.themeToken,
    async (newToken) => {
      if (newToken) {
        await initializeForMasterReport(newToken);
      }
    },
  );

  function goToSetup() {
    router.push({ name: "MasterReportSetup", params: { themeToken: route.params.themeToken } });
  }

  function handleAddTemplate() {
    const newTemplate = addBlankTemplate();
    selectTemplate(newTemplate.id);
  }

  function handleDuplicate(templateId) {
    const duplicate = duplicateTemplate(templateId);
    if (duplicate) selectTemplate(duplicate.id);
  }
</script>

<template>
  <div>
    <!-- Loading State -->
    <LoadingState
      v-if="loading"
      class="h-[calc(100vh-100px)]" />

    <!-- No PPTX Source State -->
    <div
      v-else-if="currentMasterReport && !currentMasterReport.pptx_source_id"
      class="flex items-center justify-center h-[calc(100vh-100px)]">
      <EmptyState
        :icon="FileSpreadsheet"
        title="No PowerPoint file uploaded"
        description="Upload a PPTX file in Setup to create chart templates">
        <template #action>
          <Button
            size="sm"
            @click="goToSetup">
            Go to Setup
          </Button>
        </template>
      </EmptyState>
    </div>

    <TwoPanePage v-else-if="currentMasterReport">
      <template #left>
        <Card class="flex flex-col h-[calc(100vh-100px)] !gap-0 !py-0 overflow-hidden">
          <CardContent class="flex-1 min-h-0 p-0 overflow-y-auto">
            <ChartTemplatesList
              :templates="chartTemplates"
              :selected-id="currentChartTemplateId"
              @select="selectTemplate"
              @add="handleAddTemplate"
              @duplicate="handleDuplicate"
              @delete="removeTemplate"
              @reorder="reorderTemplates" />
          </CardContent>
        </Card>
      </template>

      <template #right>
        <div class="h-[calc(100vh-100px)] overflow-hidden">
          <ChartTemplateEditor
            v-if="currentChartTemplate"
            :template="currentChartTemplate"
            :all-charts="allCharts" />
        </div>
      </template>
    </TwoPanePage>
  </div>
</template>
